/**
 * API client for the donor bundle.
 *
 * This file is the whole trust boundary on the client side. Two things are true here and
 * enforced by ADR 0002's lint rule: this bundle holds no secret, and it never computes money.
 * It sends intent and renders what the server returns.
 */
import {
  coarsenPosition,
  contextResponseSchema,
  dafGrantResponseSchema,
  type DafGrantResponse,
  directoryResponseSchema,
  donationLinkResponseSchema,
  embedSessionResponseSchema,
  type EmbedSessionResponse,
  eventPageResponseSchema,
  type EventPageResponse,
  organizationDetailSchema,
  organizationEventsResponseSchema,
  type OrganizationEventsResponse,
  type DirectoryResponse,
  type DonationLinkResponse,
  type OrganizationDetailResponse,
  donationStatusSchema,
  donorGivingSummarySchema,
  emailVerificationStartResponseSchema,
  type EmailVerificationStartResponse,
  enrichResponseSchema,
  heldReceiptEmailResponseSchema,
  type ConsentPurposeWire,
  type DonationStatus,
  type DonorGivingSummary,
  errorResponseSchema,
  goalResponseSchema,
  type GoalResponse,
  paymentIntentResponseSchema,
  phoneVerificationStartResponseSchema,
  pledgeResponseSchema,
  quoteResponseSchema,
  type ContextResponse,
  type PaymentIntentResponse,
  type PledgeResponse,
  type QuoteResponse,
  ticketOrderResponseSchema,
  ticketPageResponseSchema,
  type TicketOrderRequest,
  type TicketOrderResponse,
  type TicketPageResponse,
  upcomingEventsResponseSchema,
  type UpcomingEventsResponse,
  publicPaymentRequestResponseSchema,
  type PublicPaymentRequestResponse,
  membershipPulseResponseSchema,
  type MembershipPulseResponse,
  publicInviteResponseSchema,
  type PublicInviteResponse,
  EVENT_ACCESS_HEADER,
  eventAccessByLocationResponseSchema,
  type EventAccessByLocationResponse,
  waitlistConfirmResponseSchema,
  type WaitlistConfirmRequest,
  type WaitlistConfirmResponse,
  waitlistStartResponseSchema,
  type WaitlistStartRequest,
  type WaitlistStartResponse,
} from "@givehere/contracts";
import { storedToken } from "./donor-session.js";
import {
  eventAccessHeaders,
  forgetEventAccess,
  markMembershipGateSeen,
  membershipGateSeen,
} from "./event-access.js";
import { linkTag, referralCode } from "./gift-attribution.js";

const API_BASE: string = import.meta.env["VITE_API_BASE_URL"] ?? "http://localhost:8080";

export class ApiError extends Error {
  readonly code: string;
  readonly retryability: string;
  /** The HTTP status, or 0 when nothing came back. A 404 on a membership route is its gate off. */
  readonly status: number;
  constructor(code: string, message: string, retryability: string, status = 0) {
    super(message);
    this.code = code;
    this.retryability = retryability;
    this.status = status;
  }
}

/**
 * Every way a request can fail arrives as an `ApiError`, including the ways the server never
 * heard about.
 *
 * Each screen reports a failure by preferring `ApiError.message` and falling back to a sentence
 * of its own — "This link is not valid.", "Payment could not be started." Those fallbacks are
 * only ever reached when the rejection is *not* an `ApiError`, which is precisely the case where
 * the client knows least: nothing came back. So every one of them was a guess standing in for a
 * dropped connection, and each guess named something else as the cause — a donor whose signal
 * went is told the code on the wall is bad, or that a payment they may not have started failed.
 *
 * Converting here rather than at the nine call sites is deliberate. The screens already prefer
 * the server's words; giving them true words is enough, and it means the next screen inherits
 * this instead of inventing a tenth guess.
 */
async function request<T>(
  path: string,
  init: RequestInit,
  parse: (value: unknown) => T,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { "content-type": "application/json", ...init.headers },
    });
  } catch (caught: unknown) {
    /*
      `fetch` rejects with a TypeError for offline, DNS failure, a refused connection and CORS —
      it does not reject for any HTTP status. Narrowing to TypeError keeps a genuine bug in this
      file from being dressed up as a network problem: a donor told "check your connection"
      retries, fails identically, and nothing ever surfaces the real cause.
    */
    if (caught instanceof TypeError) {
      throw new ApiError("network_unavailable", "Could not reach GiveHere.", "retryable");
    }
    throw caught;
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = errorResponseSchema.safeParse(payload);
    if (parsed.success) {
      throw new ApiError(
        parsed.data.error.code,
        parsed.data.error.message,
        parsed.data.error.retryability,
        response.status,
      );
    }
    throw new ApiError("internal_error", "Something went wrong.", "retryable", response.status);
  }

  try {
    return parse(payload);
  } catch {
    /*
      A 200 whose body does not match the contract: a captive portal's login page, a proxy error
      served as HTML, a deploy where the two sides disagree about a field. The donor cannot act
      on a ZodError, and it is the wrong thing to put in front of them on a screen about money.
    */
    throw new ApiError("unreadable_response", "Could not read the response.", "retryable");
  }
}

/* ------------------------------------------- reads the membership gate decides (GH-871b) */

/*
 * With `FEATURE_MEMBERSHIP` on (DEC-052), the server shows an event's page, total and prompts only
 * to a signed-in member or to the holder of that event's access token, and strips events from
 * every listing for anyone else (`eventsHidden: true`). It learns "member" from the donor's own
 * bearer, which these public reads never used to carry. So:
 *
 *  - **Reads with no position** (the event page, the goal, an organization's detail and events)
 *    carry the donor's session whenever this device holds one, and the event's access token where
 *    one is held. With membership off the server never reads either, and answers exactly as
 *    before.
 *  - **Listings that carry a position** (nearby, upcoming, search) carry the session **only once
 *    the server has said membership decides the answer** — `eventsHidden` on an answer, then one
 *    retry with the session, and from then on in this tab. SPEC.md §6.2 keeps a position apart
 *    from an identity; with membership off (live) the two are never joined, as before.
 */

type HeaderMap = Record<string, string>;

/** The donor's session as a bearer, when this device holds one. */
function sessionHeader(): HeaderMap {
  const token = storedToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

/** `{ headers }` only when there is something to send, so an unchanged request stays unchanged. */
function withSession(extra: HeaderMap): { headers?: HeaderMap } {
  const headers = { ...extra, ...sessionHeader() };
  return Object.keys(headers).length > 0 ? { headers } : {};
}

/** One event's page, goal or prompts: its access token and the session, where held. */
async function eventRead<T>(
  eventId: string,
  path: string,
  parse: (value: unknown) => T,
): Promise<T> {
  const access = eventAccessHeaders(eventId);
  try {
    return await request(path, { method: "GET", ...withSession(access) }, parse);
  } catch (caught: unknown) {
    if (caught instanceof ApiError && caught.code === "event_access_required") {
      markMembershipGateSeen();
      // A token the server refused (lapsed, or for another event) is not worth sending again.
      if (access[EVENT_ACCESS_HEADER]) forgetEventAccess(eventId);
    }
    throw caught;
  }
}

/** A listing: plain until the server says membership decides it, then with the session. */
async function listingRead<T extends { eventsHidden?: true | undefined }>(
  path: string,
  parse: (value: unknown) => T,
): Promise<T> {
  const session = sessionHeader();
  const carried = membershipGateSeen() && Object.keys(session).length > 0;
  const first = await request(
    path,
    { method: "GET", ...(carried ? { headers: session } : {}) },
    parse,
  );
  if (first.eventsHidden !== true) return first;
  markMembershipGateSeen();
  if (carried || Object.keys(session).length === 0) return first;
  return request(path, { method: "GET", headers: session }, parse);
}

export const fetchContext = (
  token: string,
  /** The pay link this checkout came from (GH-845): keeps the paying rails on for its request. */
  paymentRequestToken: string | null = null,
): Promise<ContextResponse> =>
  request(
    `/v1/public/context/${encodeURIComponent(token)}${
      paymentRequestToken ? `?paymentRequest=${encodeURIComponent(paymentRequestToken)}` : ""
    }`,
    { method: "GET" },
    (value) => contextResponseSchema.parse(value),
  );

/**
 * What a pay link opens (GH-845): the request's organization, remaining amount, purpose and note,
 * and a freshly signed checkout context — or that it is paid, or no longer active.
 */
export const fetchPaymentRequest = (token: string): Promise<PublicPaymentRequestResponse> =>
  request(`/v1/public/payment-requests/${encodeURIComponent(token)}`, { method: "GET" }, (value) =>
    publicPaymentRequestResponseSchema.parse(value),
  );

/**
 * Capture a DAF grant after Chariot's component reports success. The optional donor bearer
 * links the grant to a recognized donor, exactly like donation intents.
 */
export const createDafGrant = (
  body: { contextToken: string; workflowSessionId: string; amountMinor: string },
  donorToken: string | null = null,
): Promise<DafGrantResponse> =>
  request(
    "/v1/public/daf-grants",
    {
      method: "POST",
      body: JSON.stringify(body),
      ...(donorToken ? { headers: { authorization: `Bearer ${donorToken}` } } : {}),
    },
    (value) => dafGrantResponseSchema.parse(value),
  );

export const createQuote = (
  body: {
    contextToken: string;
    amountMinor: string;
    methodClass: "card" | "wallet" | "ach";
    costSupportSelected: boolean;
    recognition: { mode: "anonymous" } | { mode: "donor_name"; displayName: string };
    /**
     * What the donor chose to support, or null for "they did not choose" (DEC-043).
     *
     * Only the identifier travels. The server resolves the default, refuses one belonging to
     * another organization, refuses an archived one, and refuses any value at all where the event
     * locks the designation — so null here means *ask the server*, never *use General*.
     */
    designationId?: string | null;
    /**
     * The donor's own promise this gift keeps (GH-809). Only the identifier travels; the server
     * refuses it without the donor's session, for a pledge that is not theirs, and for one that is
     * no longer open — so it is sent only together with `donorToken` below.
     */
    pledgeId?: string;
    /**
     * The pay link this gift answers (GH-845). The server prices the gift at what the request
     * still asks and ignores `amountMinor` and `designationId` when this is present.
     */
    paymentRequestToken?: string;
    /**
     * The appeal rung this gift or pledge answers (GH-828, GH-937). Only the identifier travels;
     * the server refuses an unknown rung, an appeal that is not running, a rung the room has
     * moved past, an `amountMinor` that is not the rung's amount, and a rung beside `pledgeId`.
     */
    appealRungId?: string;
  },
  donorToken: string | null = null,
): Promise<QuoteResponse> =>
  request(
    "/v1/donations/quote",
    {
      method: "POST",
      body: JSON.stringify(body),
      ...(donorToken ? { headers: { authorization: `Bearer ${donorToken}` } } : {}),
    },
    (value) => quoteResponseSchema.parse(value),
  );

/**
 * The idempotency key is generated once per checkout attempt and reused across retries, which is
 * what makes a double tap safe. Generating a fresh key per click would defeat the entire
 * mechanism, so it is created by the caller and passed in deliberately.
 */
/** Tells the server Plaid Link finished; the server reads the outcome from Plaid itself. */
export const completeBankLink = (
  donationId: string,
  accessToken: string,
  /**
   * Link's public token, when available. The server exchanges it with Plaid to retain the
   * connection for one-tap reuse (GH-331); this bundle holds it for exactly one request and
   * never stores it.
   */
  publicToken: string | null = null,
  /** The account Link reported; the server honours it only if the item actually holds it. */
  accountId: string | null = null,
): Promise<{ state: "processing" | "failed" | "pending" }> =>
  request(
    `/v1/donations/${encodeURIComponent(donationId)}/bank-link`,
    {
      method: "POST",
      headers: { authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({
        ...(publicToken ? { publicToken } : {}),
        ...(accountId ? { accountId } : {}),
      }),
    },
    (value) => value as { state: "processing" | "failed" | "pending" },
  );

export const createPaymentIntent = (
  body: {
    quoteToken: string;
    displayedTotal: { amountMinor: string; currency: "USD" };
    /** The button the donor pressed, so their next Give button can lead with it. */
    chosenMethod?: "card" | "apple_pay" | "google_pay";
    /** One-tap reuse of a saved instrument; requires the donor session bearer alongside. */
    savedInstrumentId?: string;
    /**
     * A note for the organization's staff (GH-793, GH-809). Never sent for a gift held for an
     * unclaimed organization, which the server refuses a note on. Part of the request body, so a
     * changed note is a new request and needs its own idempotency key (`payment-keys.ts`).
     */
    donorNote?: string | null;
    /**
     * The donor ticked "Show on the event screen" beside the note (GH-887). Sent only as `true`
     * and only with a note; also part of the body, so it is part of the idempotency key.
     */
    showNoteOnScreen?: true;
  },
  idempotencyKey: string,
  /** The donor's remembered session, when one exists: links the gift to their own profile. */
  donorToken?: string | null,
): Promise<PaymentIntentResponse> =>
  request(
    "/v1/donations/payment-intents",
    {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "idempotency-key": idempotencyKey,
        ...(donorToken ? { authorization: `Bearer ${donorToken}` } : {}),
      },
    },
    (value) => paymentIntentResponseSchema.parse(value),
  );

export const newIdempotencyKey = (): string => globalThis.crypto.randomUUID();

/* ------------------------------------------------------------------ receipt */

/**
 * The access token is a bearer credential scoped to one donation. It goes in a header rather
 * than the URL so it does not end up in browser history or a referrer (SPEC.md §11.2).
 */
const authed = (accessToken: string) => ({ authorization: `Bearer ${accessToken}` });

export const fetchDonationStatus = (
  donationId: string,
  accessToken: string,
): Promise<DonationStatus> =>
  request(
    `/v1/donations/${donationId}/status`,
    { method: "GET", headers: authed(accessToken) },
    (value) => donationStatusSchema.parse(value),
  );

/**
 * The donor's own lifetime aggregate, shown after a gift. Requires the phone-verified donor
 * session token — never the per-donation access token — because giving history is the protected
 * surface (SPEC.md §12.3) and only the proven owner of the profile may see even the aggregate.
 */
export const fetchGivingSummary = (donorToken: string): Promise<DonorGivingSummary> =>
  request(
    "/v1/donor/summary",
    { method: "GET", headers: { authorization: `Bearer ${donorToken}` } },
    (value) => donorGivingSummarySchema.parse(value),
  );

export const enrichReceipt = (
  receiptId: string,
  accessToken: string,
  body: {
    displayName?: string;
    email?: string;
    phone?: string;
    consents?: ConsentPurposeWire[];
  },
): Promise<{ saved: boolean; consentsRecorded: number }> =>
  request(
    `/v1/receipts/${receiptId}/enrich`,
    { method: "POST", body: JSON.stringify(body), headers: authed(accessToken) },
    (value) => enrichResponseSchema.parse(value),
  );

/**
 * GH-947: a guest's receipt address while the gift is held for the end of the day. There is no
 * receipt yet for `enrichReceipt` to address; the server keeps the address and emails the receipt
 * when the gift is charged. Authorized by the donation's own token, like the status poll.
 */
export const saveHeldReceiptEmail = (
  donationId: string,
  accessToken: string,
  body: { email: string; displayName?: string },
): Promise<{ saved: true }> =>
  request(
    `/v1/donations/${donationId}/receipt-email`,
    { method: "POST", body: JSON.stringify(body), headers: authed(accessToken) },
    (value) => heldReceiptEmailResponseSchema.parse(value),
  );

/* ------------------------------------------------------- phone verification */

export const startPhoneVerification = (
  phone: string,
): Promise<{ verificationId: string; last4: string; developmentCode: string | null }> =>
  request(
    "/v1/contacts/phone-verifications",
    { method: "POST", body: JSON.stringify({ phone }) },
    (value) => phoneVerificationStartResponseSchema.parse(value),
  );

/**
 * The same challenge on the other channel (TestFlight feedback, 2026-09-15: "some people want
 * email, it can be verified that way"). No `last4` in the answer — the donor typed the address and
 * there is nothing to mask on the screen that shows it back.
 */
export const startEmailVerification = (email: string): Promise<EmailVerificationStartResponse> =>
  request(
    "/v1/contacts/email-verifications",
    { method: "POST", body: JSON.stringify({ email }) },
    (value) => emailVerificationStartResponseSchema.parse(value),
  );

/* ------------------------------------------------------------------ pledge */

/**
 * Where the reminder goes: at most one of the two, which is what the contract's refinement checks
 * server-side and what the `never` on the other half says at compile time. Neither is only for a
 * signed-in donor with no challenge (GH-939); the server refuses it otherwise.
 */
export type PledgeContact =
  | { phone: string; email?: never }
  | { email: string; phone?: never }
  // GH-939: neither, for a signed-in donor — the server resolves the session's verified contact.
  | { phone?: never; email?: never };

/**
 * Records a promise, not a payment.
 *
 * The verification id and code go to the server together with the pledge, because the server
 * consumes the challenge itself. There is deliberately no client-side "verified" flag to send —
 * one would be an assertion, and this browser cannot make assertions the server acts on.
 *
 * Both may be absent together (the contract's own refinement) for a signed-in donor whose
 * profile already holds the address as a verified contact: the body still names the address and
 * the **server** resolves the proof from the session, or refuses — the one-tap appeal pledge
 * (owner 2026-10-10). `donorToken` carries that session; without it the challenge pair is the
 * only way through. Since GH-939 the address may be absent too, with no challenge: the server
 * takes the session profile's own verified contact (phone first, else email) or refuses — the
 * phone-only donor's one click, since this bundle holds only `phoneLast4`.
 */
export const createPledge = (
  body: {
    quoteToken: string;
    displayedAmount: { amountMinor: string; currency: "USD" };
    verificationId?: string;
    code?: string;
    displayName?: string;
  } & PledgeContact,
  donorToken: string | null = null,
): Promise<PledgeResponse> =>
  request(
    "/v1/pledges",
    {
      method: "POST",
      body: JSON.stringify(body),
      ...(donorToken ? { headers: { authorization: `Bearer ${donorToken}` } } : {}),
    },
    (value) => pledgeResponseSchema.parse(value),
  );

/**
 * Stands in for the bank confirming the payment. Only exists when the API is running on the
 * synthetic rail; the build flag keeps the button out of any bundle that talks to a real one.
 */
export const SYNTHETIC_PAYMENTS: boolean = import.meta.env["VITE_SYNTHETIC_PAYMENTS"] === "true";

export const confirmSyntheticPayment = (
  donationId: string,
  accessToken: string,
): Promise<unknown> =>
  request(
    `/v1/dev/donations/${donationId}/confirm`,
    { method: "POST", headers: authed(accessToken) },
    (value) => value,
  );

/* ---------------------------------------------------------------- directory */

/**
 * Nearest organizations.
 *
 * The position is **coarsened before it leaves this function**, not by the caller. Rounding at
 * the point of transmission means no future caller can pass a precise fix by forgetting a step,
 * and the server coarsens again on arrival — two independent reductions, because
 * `SPEC.md §6.2` promises the precise value is discarded and the most reliable way to discard
 * something is never to send it.
 */
export const fetchNearby = (
  position: { latitude: number; longitude: number } | null,
  limit: number,
): Promise<DirectoryResponse> => {
  const query = new URLSearchParams({ limit: String(limit) });
  if (position) {
    const coarse = coarsenPosition(position);
    query.set("lat", String(coarse.latitude));
    query.set("lon", String(coarse.longitude));
  }
  return listingRead(`/v1/public/organizations/nearby?${query}`, (value) =>
    directoryResponseSchema.parse(value),
  );
};

/**
 * Announced events that have not started, near a coarse position (GH-800, GH-809).
 *
 * Public and identifier-free like `fetchNearby`, and coarsened here for the same reason. The
 * "signed in only" rule is the screen's (`Home.tsx`). The position is joined to a session only
 * where the server has said membership decides the list (GH-871b, `listingRead`), never with
 * membership off.
 */
export const fetchUpcomingEvents = (position: {
  latitude: number;
  longitude: number;
}): Promise<UpcomingEventsResponse> => {
  const coarse = coarsenPosition(position);
  const query = new URLSearchParams({
    lat: String(coarse.latitude),
    lon: String(coarse.longitude),
  });
  return listingRead(`/v1/public/events/upcoming?${query}`, (value) =>
    upcomingEventsResponseSchema.parse(value),
  );
};

export const searchOrganizations = (
  text: string,
  position: { latitude: number; longitude: number } | null,
): Promise<DirectoryResponse> => {
  const query = new URLSearchParams({ q: text, limit: "25" });
  if (position) {
    const coarse = coarsenPosition(position);
    query.set("lat", String(coarse.latitude));
    query.set("lon", String(coarse.longitude));
  }
  return listingRead(`/v1/public/organizations/search?${query}`, (value) =>
    directoryResponseSchema.parse(value),
  );
};

/**
 * Ask the server to mint a checkout link for a live event.
 *
 * The directory cannot build one itself — only the server signs context — so a card tap fetches
 * this and then navigates. Fails for an organization that cannot accept donations, which is the
 * same refusal checkout itself would give, just earlier and cheaper.
 */
export const fetchDonationLink = (eventId: string): Promise<DonationLinkResponse> =>
  request(
    // `via` (GH-841): how this visit arrived — a table's code, the big screen's, or a shared link —
    // so the organization's Home can say where gifts come from. Attribution only. `ref` (GH-864):
    // a member's share code this device kept from their link — the server re-validates it and an
    // unknown one simply earns no credit.
    `/v1/public/events/${encodeURIComponent(eventId)}/donation-link?${donationLinkQuery()}`,
    { method: "GET" },
    (value) => donationLinkResponseSchema.parse(value),
  );

/** `via`, then `ref` only where this device kept a live share code. */
function donationLinkQuery(): string {
  const query = new URLSearchParams({ via: linkTag() });
  const ref = referralCode();
  if (ref !== null) query.set("ref", ref);
  return query.toString();
}

/**
 * The embeddable give box's checkout context (GH-847), minted after the frame loads so the
 * address pasted onto somebody else's page never carries a token. The token is kept in the frame's
 * memory and spent on the ordinary quote and payment routes. The answer also carries the
 * organization's brand color, the keyline's tint when the snippet names none (GH-850). Any site may
 * frame the box, so the frame no longer reports who framed it.
 */
export const mintEmbedSession = (body: {
  organizationId: string;
  eventId?: string;
  /** A saved widget (GH-851): the server reads its config and holds the gift to it. */
  widgetId?: string;
  /** The console's draft preview (GH-851); draws the box and takes no gift. */
  previewToken?: string;
}): Promise<EmbedSessionResponse> =>
  request("/v1/public/embed/sessions", { method: "POST", body: JSON.stringify(body) }, (value) =>
    embedSessionResponseSchema.parse(value),
  );

/**
 * Ask the server to mint a checkout link for a gift held for an organization that has not claimed
 * its listing (GH-798, DEC-046).
 *
 * Keyed by the organization, not an event: the server picks the holding fund itself, and this
 * bundle never names one. Refused with `capability_disabled` while `FEATURE_UNCLAIMED_ORG_GIFTS`
 * is off — the caller falls back to the unclaimed wording rather than reporting an error.
 */
export const fetchHeldGiftLink = (organizationId: string): Promise<DonationLinkResponse> =>
  request(
    `/v1/public/organizations/${encodeURIComponent(organizationId)}/held-gift-link`,
    { method: "GET" },
    (value) => donationLinkResponseSchema.parse(value),
  );

/**
 * The public goal projection for one event — received, pledged, processing, and the count.
 *
 * The same read the wall at the back of the hall performs, and identifier-free for the same
 * reason: it is a projection, not a query about a donor. The landing page shows it so somebody
 * deciding whether to give can see whether anyone else has.
 */
export const fetchGoal = (eventId: string): Promise<GoalResponse> =>
  eventRead(eventId, `/v1/public/events/${encodeURIComponent(eventId)}/goal`, (value) =>
    goalResponseSchema.parse(value),
  );

/**
 * Who is holding this event, and what it is (GH-781).
 *
 * The identity half of the event page, kept apart from `fetchGoal` because the two move at
 * completely different speeds: this is a name, a mark and a purpose, read once and cached a
 * minute, while the goal is a number polled every few seconds. Merging them would put an
 * organization id and a logo on the same fast path the hall's wall reads, which is a payload
 * whose narrowness is the reason it is safe to project.
 *
 * It mints nothing. `fetchDonationLink` is the call that signs a checkout context, and it is made
 * when the donor presses Give and at no other time.
 */
export const fetchEventPage = (eventId: string): Promise<EventPageResponse> =>
  eventRead(eventId, `/v1/public/events/${encodeURIComponent(eventId)}`, (value) =>
    eventPageResponseSchema.parse(value),
  );

/** GH-871a: with membership on, a non-member's answer carries no event (`event: null`). */
export const fetchOrganizationDetail = (
  organizationId: string,
): Promise<OrganizationDetailResponse> =>
  request(
    `/v1/public/organizations/${encodeURIComponent(organizationId)}`,
    { method: "GET", ...withSession({}) },
    (value) => organizationDetailSchema.parse(value),
  );

/**
 * What this organization has on, and what it has just finished (GH-770).
 *
 * A second request rather than a bigger detail payload, and the split is deliberate: the
 * organization page's Give button is decided entirely by the detail response, so a slow or failed
 * list read leaves a page that can still take a gift. Callers should render the lists as absent
 * on failure rather than failing the page — the same treatment `fetchGoal` already gets.
 */
export const fetchOrganizationEvents = (
  organizationId: string,
): Promise<OrganizationEventsResponse> =>
  request(
    `/v1/public/organizations/${encodeURIComponent(organizationId)}/events`,
    { method: "GET", ...withSession({}) },
    (value) => organizationEventsResponseSchema.parse(value),
  );

/*
 * Check-in and engagement do **not** live here. They live in `donor-session.ts` (GH-782).
 *
 * They were written in both places: this module gained seven functions taking a bearer as an
 * argument (GH-780) while `donor-session.ts` already had the same seven taking it from the module
 * that owns it. Nothing ever called this copy — every screen reached for the other — and two
 * clients for one route is how a header, a schema or an error branch gets fixed in only one of
 * them. `donor-session.ts` is the surviving home because it is the module that reads and writes
 * the token; a caller here would have had to fetch the token from there and hand it back.
 *
 * The two response schemas this copy validated and the other did not (`upvoteResponseSchema` and
 * the reaction acknowledgement) moved across rather than being dropped.
 */

/**
 * Ask the browser where it is.
 *
 * Requested at the moment of use behind an explicit control, never on load (SPEC.md §6.2) — a
 * page that prompts for location the instant it opens teaches people to refuse the prompt.
 * Resolves to null on denial, timeout, or an unsupported browser, all of which are ordinary and
 * all of which fall back to search.
 */
export function requestApproximatePosition(): Promise<{
  latitude: number;
  longitude: number;
} | null> {
  if (!("geolocation" in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (fix) => resolve({ latitude: fix.coords.latitude, longitude: fix.coords.longitude }),
      () => resolve(null),
      // Low accuracy on purpose: the answer is rounded to about a kilometre anyway, and asking
      // for high accuracy powers up the GPS and takes seconds for precision that is discarded.
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300_000 },
    );
  });
}

/* --------------------------------------------------------- ticketing (DEC-016) */

export const fetchTicketPage = (eventId: string): Promise<TicketPageResponse> =>
  request(`/v1/public/events/${encodeURIComponent(eventId)}/tickets`, { method: "GET" }, (value) =>
    ticketPageResponseSchema.parse(value),
  );

export const createTicketOrder = (
  eventId: string,
  body: TicketOrderRequest,
  idempotencyKey: string,
  /** The donor's session bearer, when remembered — links the order and enables saved cards. */
  donorToken: string | null = null,
): Promise<TicketOrderResponse> =>
  request(
    `/v1/public/events/${encodeURIComponent(eventId)}/ticket-orders`,
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey,
        ...(donorToken ? { authorization: `Bearer ${donorToken}` } : {}),
      },
      body: JSON.stringify(body),
    },
    (value) => ticketOrderResponseSchema.parse(value),
  );

export const fetchTicketOrder = (
  orderId: string,
  accessToken: string,
): Promise<TicketOrderResponse> =>
  request(
    `/v1/public/ticket-orders/${encodeURIComponent(orderId)}`,
    { method: "GET", headers: { authorization: `Bearer ${accessToken}` } },
    (value) => ticketOrderResponseSchema.parse(value),
  );

/** Where the API serves a payload-relative asset like the event hero image. */
export const apiUrl = (path: string): string => `${API_BASE}${path}`;

/** Images may be API-relative paths (uploaded logos/heroes) or absolute https URLs (external). */
export const assetUrl = (url: string): string => (url.startsWith("/") ? apiUrl(url) : url);

/* ------------------------------------------------- membership, public half (GH-864e) */

/**
 * `null` when the route answers 404: `FEATURE_MEMBERSHIP` is off (or the API predates it), and
 * every membership surface on this bundle hides. Any other failure is still a failure.
 */
async function unlessGateOff<T>(read: Promise<T>): Promise<T | null> {
  try {
    return await read;
  } catch (caught: unknown) {
    if (caught instanceof ApiError && caught.status === 404) return null;
    throw caught;
  }
}

/** "Over 1,200 waiting", or null below the threshold — and the whole answer null when gated off. */
export const fetchMembershipPulse = (): Promise<MembershipPulseResponse | null> =>
  unlessGateOff(
    request("/v1/public/membership/pulse", { method: "GET" }, (value) =>
      membershipPulseResponseSchema.parse(value),
    ),
  );

/**
 * What `/i/<code>` shows before anyone signs in. One shape for unknown, used, revoked and expired
 * — `unavailable` — so the page cannot be used to probe which codes exist. `null` when gated off.
 */
export const fetchPublicInvite = (code: string): Promise<PublicInviteResponse | null> =>
  unlessGateOff(
    // GH-864h SEC-M3: the code goes in the body, so no request log carries it.
    request(
      "/v1/public/invites/lookup",
      { method: "POST", body: JSON.stringify({ code }) },
      (value) => publicInviteResponseSchema.parse(value),
    ),
  );

/* ----------------------------------------------- invite-only access (GH-871b, DEC-052) */

/**
 * At the event: is there an open event here, and if so, its access token.
 *
 * The position is **coarsened before it leaves this function** (two decimals, about a kilometre),
 * the same rule as `fetchNearby`, and it travels in the body so no request log carries it. The
 * server rounds again, uses it for one query and drops it. `null` when the route answers 404
 * (membership off), which no screen reaches, since only a gated answer offers this.
 */
export const fetchEventAccessByLocation = (position: {
  latitude: number;
  longitude: number;
}): Promise<EventAccessByLocationResponse | null> =>
  unlessGateOff(
    request(
      "/v1/public/events/access-by-location",
      { method: "POST", body: JSON.stringify(coarsenPosition(position)) },
      (value) => eventAccessByLocationResponseSchema.parse(value),
    ),
  );

/** The waitlist without an account: a code to a phone or an inbox. One answer for anyone. */
export const startAccountlessWaitlist = (
  body: WaitlistStartRequest,
): Promise<WaitlistStartResponse> =>
  request("/v1/public/waitlist/start", { method: "POST", body: JSON.stringify(body) }, (value) =>
    waitlistStartResponseSchema.parse(value),
  );

/** Prove the contact and take (or keep) a place in line. No account is created. */
export const confirmAccountlessWaitlist = (
  body: WaitlistConfirmRequest,
): Promise<WaitlistConfirmResponse> =>
  request("/v1/public/waitlist/confirm", { method: "POST", body: JSON.stringify(body) }, (value) =>
    waitlistConfirmResponseSchema.parse(value),
  );

export type PositionForAccess =
  | { readonly kind: "position"; readonly latitude: number; readonly longitude: number }
  | { readonly kind: "denied" }
  | { readonly kind: "unavailable" };

/**
 * Ask the browser where it is, for the at-event check — like `requestApproximatePosition`, but
 * telling a refusal apart from a failure, because the two need different sentences: "turn it on"
 * against "try again". Asked only behind the explicit "Use my location" tap (SPEC.md §6.2).
 */
export function requestPositionForAccess(): Promise<PositionForAccess> {
  if (!("geolocation" in navigator)) return Promise.resolve({ kind: "unavailable" });
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (fix) =>
        resolve({
          kind: "position",
          latitude: fix.coords.latitude,
          longitude: fix.coords.longitude,
        }),
      (failure) =>
        // 1 is PERMISSION_DENIED; timeouts and "position unavailable" are both "try again".
        resolve({ kind: failure.code === 1 ? "denied" : "unavailable" }),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300_000 },
    );
  });
}
